import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import feedbackService from "../../services/feedbackService";
import { useLanguage } from "../../contexts/LanguageContext";
import { getTranslation } from "../../translations";
import {
  Box,
  Flex,
  Text,
  Button,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Textarea,
  useDisclosure,
  Icon,
  useToast,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";

const FeedbackSection = () => {
  const location = useLocation();
  const toast = useToast();
  const { selectedLanguage } = useLanguage();
  const [showThankYou, setShowThankYou] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Check if current page is partnerships page
  const isPartnershipsPage = location.pathname === "/partnerships-collaborations";

  // Get translations function
  const t = (path) => getTranslation(selectedLanguage, path);

  // Get feedback text based on page and language
  const getFeedbackText = (key) => {
    if (isPartnershipsPage) {
      return t(`feedback.${key}`);
    }
    // Default English text for all other pages
    switch (key) {
      case 'question':
        return "Was this information helpful to you?";
      case 'yes':
        return "Yes";
      case 'no':
        return "No";
      case 'thankYou':
        return "Thank you for your feedback!";
      case 'modalTitle':
        return "We're sorry to hear that. Please let us know how we can improve.";
      case 'placeholder':
        return "Text box - up to 250 characters";
      case 'submit':
        return "Submit";
      default:
        return "";
    }
  };

  // Create unique key for each page
  const currentPageKey = location.pathname || '/';
  
  // Check if user has already given feedback for this specific page
  useEffect(() => {
    const feedbackKey = `feedback_given_${currentPageKey}`;
    const hasGivenFeedback = sessionStorage.getItem(feedbackKey);
    
    if (hasGivenFeedback === 'true') {
      setShowThankYou(true);
    } else {
      setShowThankYou(false);
    }
  }, [currentPageKey]); // Re-run when page changes

  const handleYesFeedback = async () => {
    try {
      setIsSubmitting(true);
      
      // Submit positive feedback to backend
      await feedbackService.submitPositiveFeedback(
        currentPageKey,
        document.title
      );
      
      const feedbackKey = `feedback_given_${currentPageKey}`;
      setShowThankYou(true);
      // Save feedback state for this specific page
      sessionStorage.setItem(feedbackKey, 'true');
      
    } catch (error) {
      console.error('Error submitting positive feedback:', error);
      toast({
        title: "Error",
        description: "Failed to submit feedback. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNoFeedback = () => {
    onOpen();
  };

  const handleFeedbackSubmit = async () => {
    if (!feedbackText.trim()) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Submit negative feedback to backend
      await feedbackService.submitNegativeFeedback(
        currentPageKey,
        document.title,
        feedbackText
      );
      
      const feedbackKey = `feedback_given_${currentPageKey}`;
      // Show thank you message and save feedback state for this specific page
      setShowThankYou(true);
      sessionStorage.setItem(feedbackKey, 'true');
      
      setFeedbackText("");
      onClose();
      
    } catch (error) {
      console.error('Error submitting negative feedback:', error);
      toast({
        title: "Error",
        description: "Failed to submit feedback. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearFeedbackForCurrentPage = () => {
    const feedbackKey = `feedback_given_${currentPageKey}`;
    sessionStorage.removeItem(feedbackKey);
    setShowThankYou(false);
  };

  const handleModalClose = () => {
    setFeedbackText("");
    onClose();
  };

  return (
    <>
      {/* Feedback Section */}
      <Box bg="#EFF9FC" py={{ base: 1, md: 2 }} px={{ base: 4, md: 6, lg: 8 }}>
        <Box maxW="7xl" mx="auto">
          <Box
            p={{ base: 1, md: 2 }}
            textAlign="center"
          >
            {showThankYou ? (
              <Flex align="center" justify="center" direction="row" gap={2} position="relative">
                <Icon as={CheckIcon} color="green.500" boxSize={5} />
                <Text fontSize="lg" fontWeight="semibold" color="#000B8C">
                  {getFeedbackText('thankYou')}
                </Text>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={clearFeedbackForCurrentPage}
                  position="absolute"
                  right="-40px"
                  top="50%"
                  transform="translateY(-50%)"
                  color="gray.400"
                  _hover={{ color: "gray.600" }}
                  minW="auto"
                  h="auto"
                  p={1}
                >
                  ✕
                </Button>
              </Flex>
            ) : (
              <>
                <Text
                  fontSize="lg"
                  fontWeight="semibold"
                  mb={0}
                  color="#000B8C"
                >
                  {getFeedbackText('question')}
                </Text>
                <HStack spacing={4} justify="center">
                  <Button
                    leftIcon={<Text fontSize="lg">👍</Text>}
                    size="md"
                    onClick={handleYesFeedback}
                    bg="transparent"
                    transition="all 0.2s"
                    px={6}
                    fontWeight="normal"
                    isLoading={isSubmitting}
                    loadingText="Submitting..."
                  >
                    {getFeedbackText('yes')}
                  </Button>
                  <Button
                    leftIcon={<Text fontSize="lg">👎</Text>}
                    bg="transparent"
                    color="gray.700"
                    size="md"
                    onClick={handleNoFeedback}
                    transition="all 0.2s"
                    px={6}
                    fontWeight="normal"
                    isDisabled={isSubmitting}
                  >
                    {getFeedbackText('no')}
                  </Button>
                </HStack>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Feedback Modal */}
      <Modal isOpen={isOpen} onClose={handleModalClose} isCentered size="lg">
        <ModalOverlay bg="rgba(0, 0, 0, 0.4)" />
        <ModalContent mx={4} borderRadius="lg" maxW="2xl">
          <ModalCloseButton 
            size="lg" 
            color="gray.500"
            _hover={{ bg: "gray.100" }}
          />
          <ModalBody pt={8} pb={6} px={8}>
            <Text fontSize="lg" color="gray.800" mb={6} lineHeight="1.5">
              {getFeedbackText('modalTitle')}
            </Text>
            <Textarea
              placeholder={getFeedbackText('placeholder')}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              maxLength={250}
              rows={5}
              resize="none"
              border="2px solid"
              borderColor="gray.200"
              borderRadius="lg"
              fontSize="sm"
              color="gray.600"
              _placeholder={{ color: "gray.400" }}
              _focus={{ 
                borderColor: "blue.400",
                boxShadow: "0 0 0 1px rgba(66, 153, 225, 0.6)"
              }}
            />
          </ModalBody>
          <ModalFooter justifyContent="center" pb={8}>
            <Button
              bg="blue.600"
              color="white"
              onClick={handleFeedbackSubmit}
              isDisabled={!feedbackText.trim() || isSubmitting}
              isLoading={isSubmitting}
              loadingText="Submitting..."
              _hover={{ bg: "blue.700" }}
              _disabled={{ 
                bg: "gray.300", 
                cursor: "not-allowed",
                _hover: { bg: "gray.300" }
              }}
              size="lg"
              borderRadius="lg"
              px={12}
              py={6}
              fontSize="md"
              fontWeight="semibold"
              minW="200px"
            >
              {getFeedbackText('submit')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default FeedbackSection;
